import {
  Chip,
  ChipColorEnum,
  ChipShapeEnum,
  ChipSizeEnum,
  FontColorEnum,
  FontSizeEnum,
  IconButtonSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { useGetBroadcastScheduleById } from '@/features/broadcast-schedule-program/api/get-broadcast-schedule-by-id';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { useRelaunchClip } from '@/features/clips/api/relaunch-clip';
import { useClipController } from '@/features/clips/hooks/useClipController';
import { TClip, TPatchClip } from '@/features/clips/types';
import { TReviewStateValues } from '@/features/segments/types';
import { downloadFromUrl } from '@/helpers/download';
import { useReport } from '@/stores/report-store';

import { ClipMiniCardCardActions } from './ClipMiniCardCardActions';

type TClipMiniCardBaseCardBodyProps = {
  item: TClip;
};

export function ClipMiniCardBaseCardBody({ item }: TClipMiniCardBaseCardBodyProps) {
  const { compactUis, programNavigationState, setClipItems, setActiveClip } =
    useBroadcastScheduleProgram();
  const { mutateAsync: patchClip } = usePatchClip();
  const { setReport, setReportedId, setReportType } = useReport();
  const { clipDialog, clipRelaunchDialog, handleDeleteClip } = useClipController();
  const { t } = useTranslation();
  const { mutate: relaunchClip } = useRelaunchClip();
  const { data: programBroadcast } = useGetBroadcastScheduleById({
    id: item.program_broadcast_id || '',
  });

  const uniqueKeywords = useMemo(() => {
    const list = (item.clip_mentions || []).flatMap(mention =>
      (mention.keywords || []).map(k => (k ?? '').trim()).filter(Boolean)
    );
    // Dedupe case-insensitive pero preservando la primera forma escrita
    return Array.from(new Map(list.map(k => [k.toLowerCase(), k])).values());
  }, [item.clip_mentions]);

  const handleStatusChange = (status: string) => {
    if (status === '5') {
      setReportType('clip');
      setReport(true);
      setReportedId(item.id);
      return;
    }
    const body: TPatchClip = {
      id: item?.id || '',
      review_state: parseInt(status) as TReviewStateValues,
      media_type: item?.media_type || '',
    };

    patchClip(body).then(data => {
      setActiveClip(prev => {
        if (!prev) return prev;
        if (prev.id === data.id) {
          return { ...prev, review_state: data.review_state };
        }
        return prev;
      });
      setClipItems(prev => {
        if (!prev) return prev;
        return prev.map(clipItem => {
          if (clipItem.id === data.id) {
            return {
              ...clipItem,
              review_state: data.review_state,
            };
          }
          return clipItem;
        });
      });
    });
  };

  const handleGoToProgram = item.program_broadcast_id
    ? () => {
        const hasProgramNavigationState =
          programNavigationState !== null &&
          programNavigationState.id === item.program_broadcast_id;
        const gte = hasProgramNavigationState
          ? programNavigationState.gte
          : programBroadcast?.start_datetime;
        const lte = hasProgramNavigationState
          ? programNavigationState.lte
          : programBroadcast?.end_datetime;

        const params = new URLSearchParams();
        if (gte) params.set('gte', gte);
        if (lte) params.set('lte', lte);

        window.open(
          `/app/review/broadcast_schedule/${item.program_broadcast_id}?${params.toString()}`,
          '_blank',
          'noopener,noreferrer'
        );
      }
    : undefined;

  const handleOpenDialog = (item: string) => {
    clipDialog.open(item);
  };

  return (
    <>
      <div className="flex w-full flex-1 flex-col">
        {item.body && !compactUis && (
          <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Medium}>
            {item.body}
          </Typography>
        )}
        {!compactUis && (
          <div className="flex flex-wrap gap-2">
            {uniqueKeywords.map(keyword => (
              <Chip
                shape={ChipShapeEnum.ROUNDED}
                size={ChipSizeEnum.SM}
                color={ChipColorEnum.NEUTRAL_0}
                key={keyword}
                className="!rounded-md"
                textClassName="!whitespace-normal"
              >
                {keyword}
              </Chip>
            ))}
          </div>
        )}
        <div
          data-id="clip-card-actions"
          className={clsx(
            'flex justify-end transition duration-500 ease-linear group-hover/broadcasterCard:max-h-[500px] group-hover/broadcasterCard:opacity-100 md:opacity-0',
            compactUis && '!md:max-h-0'
          )}
        >
          <ClipMiniCardCardActions
            item={item}
            onGoToProgram={handleGoToProgram}
            onReview={handleStatusChange}
            onDelete={handleOpenDialog}
            onDownload={downloadFromUrl}
            onRelaunch={clipRelaunchDialog.open}
            size={IconButtonSizeEnum.SM}
            className="bg-neutral-20"
          />
        </div>
      </div>
      <ModalDialog
        isOpen={clipDialog.isOpen}
        onClose={clipDialog.close}
        onAction={() => handleDeleteClip(item.id)}
        heading={t('Eliminar clip')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar el clip seleccionado?')} />
      </ModalDialog>

      <RelaunchEnrichmentsModal
        isOpen={clipRelaunchDialog.isOpen}
        onClose={clipRelaunchDialog.close}
        onAction={enrichments => {
          const toId = clipRelaunchDialog.data || item.id;
          relaunchClip({ id: toId, enrichments });
          clipRelaunchDialog.close();
        }}
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        allowAdvertising={false}
        isRadio={!item.media_type.includes(ChannelMediaTypeEnum.TV)}
      />
    </>
  );
}
